"use client";

import React, { useState } from "react";
import { Box, Dialog, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import useSearchManager from "@/hooks/tour-search/use-search-manager";
import { convertSearchFiltersToAppliedFilters } from "@/factories/search-filters/search-parameters-factory";
import TourSearchResults from "@/components/find-your-tour-page/tour-search-results";
import AppliedFilters from "@/components/search-filters/applied-filters";
import RequestAQuoteSection from "@/components/forms/request-a-quote/request-a-quote-section";
import { TourSearchResult } from "@/types/search/tour-search-results";
import useFragmentParser from "@/hooks/tour-search/use-fragment-parser";
import { useUserGroups } from "@/hooks/login/useUserGroups";
import useSearchDataLayer from "@/hooks/analytics/use-search-data-layer";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import SearchFilters from "../search-filters/filter-layout";
import MobileSearchHeader from "../search-filters/mobile-search-header";
import SortOrder from "../search-filters/sort-order";
import DataLayer from "../data-layer/data-layer";

interface FindYourTourLayoutProps {
  initialSearchParams?: { [key: string]: string | string[] | undefined };
  headerText?: string;
}

const FindYourTourLayout = ({ initialSearchParams, headerText }: FindYourTourLayoutProps) => {
  const t = useTranslations("SEARCH");

  // Get user groups for role-based logic
  const { userGroups } = useUserGroups();

  // All search logic is now contained in this one hook
  const {
    isLoading,
    isError,
    searchFilters,
    filtersData,
    sortOptions,
    rangesData,
    searchResults,
    searchSettings,
    searchSource,
    handleFilterChange,
    handleSortChange,
    handlePageChange,
    handleClearAll,
    handleFilterDelete,
    handleError
  } = useSearchManager(initialSearchParams);

  // DataLayer analytics logic is contained in this hook
  const {
    searchResultsDataLayer,
    filterDataLayerData,
    searchDataLayerData,
    handleFilterInteraction
  } = useSearchDataLayer({
    searchResults,
    searchSettings,
    initialSearchParams,
    searchSource,
    isLoading,
    isError,
    userGroups
  });

  // Add dialog state for mobile filter
  const [filterDialogOpen, setFilterDialogOpen] = React.useState(false);
  const [sortDialogOpen, setSortDialogOpen] = React.useState(false);

  useFragmentParser();

  // state for request a quote modal
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [openTour, setOpenTour] = useState<TourSearchResult | null>(null);
  

  useScrollLock(formOpen);

  const handleClickOpen = (tour: TourSearchResult) => {
    setFormOpen(true);
    setOpenTour(tour);
  };

  const handleClose = () => {
    setFormOpen(false);
    setOpenTour(null);
  };

  return (
    <Box sx={{ display: "flex", width: "100vw", justifyContent: "center" }}>
      {/* DataLayer for initial search results */}
      {searchResultsDataLayer && <DataLayer data={searchResultsDataLayer} />}

      {/* DataLayer for search events */}
      {searchDataLayerData && <DataLayer data={searchDataLayerData} />}

      {/* DataLayer for filter interactions */}
      {filterDataLayerData && <DataLayer data={filterDataLayerData} />}

      <Stack
        sx={{
          padding: {
            zero: "4rem 2.4rem",
            legacyMobile: "4rem 2.4rem",
            legacyTablet: "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
          },
          gap: "2rem",
          maxWidth: "144rem",
          width: "100%"
        }}
      >
        <Stack flexDirection="row" gap="4rem" width="100%" alignItems="flex-start">
          <Box
            sx={{
              maxWidth: 300,
              width: "100%",
              display: { xs: "none", legacyDesktop: "flex" },
              flexDirection: "column",
              flexShrink: 0
            }}
          >
            <SearchFilters
              filters={filtersData}
              ranges={rangesData}
              currentSearchParams={searchFilters}
              onFiltersChange={handleFilterChange}
              onClearAll={handleClearAll}
              isLoading={isLoading}
              onFilterInteraction={handleFilterInteraction}
            />
          </Box>

          <Stack gap="2rem" width="100%" alignItems={{ xs: "flex-start", legacyDesktop: "center" }}>
            <Typography variant="h1" sx={{ alignSelf: "flex-start" }}>
              {headerText || t("Find Your Tour")}
            </Typography>
            <Box
              sx={{ display: { xs: "flex", legacyDesktop: "none" } }}
              data-testid="mobile-search-controls"
            >
              <MobileSearchHeader
                filtersData={filtersData}
                rangesData={rangesData}
                searchFilters={searchFilters}
                handleFilterChange={handleFilterChange}
                handleClearAll={handleClearAll}
                isLoading={isLoading}
                filterDialogOpen={filterDialogOpen}
                totalTours={searchSettings?.totalCount ?? 0}
                setFilterDialogOpen={setFilterDialogOpen}
                sortDialogOpen={sortDialogOpen}
                setSortDialogOpen={setSortDialogOpen}
                sortOptions={sortOptions}
                handleSortChange={handleSortChange}
                onFilterInteraction={handleFilterInteraction}
              />
            </Box>
            <Stack
              sx={{
                width: "100%",
                flexDirection: "row",
                gap: "5rem",
                justifyContent: "space-between"
              }}
              data-testid="applied-filters-and-sort"
            >
              {isLoading ? (
                <Skeleton width="15rem" />
              ) : (
                <AppliedFilters
                  resultsCount={searchSettings?.totalCount ?? 0}
                  filters={convertSearchFiltersToAppliedFilters(searchFilters, filtersData)}
                  handleDelete={handleFilterDelete}
                  onFilterInteraction={handleFilterInteraction}
                />
              )}
              <Box
                sx={{ display: { xs: "none", legacyDesktop: "flex" } }}
                data-testid="desktop-sort-controls"
              >
                <SortOrder
                  sortOptions={sortOptions}
                  onSortChange={handleSortChange}
                  loading={isLoading}
                  onFilterInteraction={handleFilterInteraction}
                />
              </Box>
            </Stack>

            <TourSearchResults
              tours={searchResults}
              isLoading={isLoading}
              isError={isError}
              searchSettings={searchSettings}
              handlePageChange={handlePageChange}
              errorOnClick={handleError}
              clearFilters={handleClearAll}
              openRequestAQuote={handleClickOpen}
              onFilterInteraction={handleFilterInteraction}
            />
          </Stack>
        </Stack>
      </Stack>
      <Dialog open={formOpen} onClose={handleClose} maxWidth="md">
        <RequestAQuoteSection
          handleClose={handleClose}
          tourId={openTour?.tourId}
          landingPageId={openTour?.landingPageId}
          tourTitle={`${openTour?.title} ${openTour?.subTitle ?? ""}`}
        />
      </Dialog>
    </Box>
  );
};

export default FindYourTourLayout;
